"use client";
import {
	faBiohazard,
	faBookOpen,
	faChessKnight,
	faDice,
	faDragon,
	faGamepad,
	faGun,
	faInfinity,
	faLink,
	faScroll,
	faSkull,
	faSpaceShuttle,
	faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion } from "framer-motion";

const LaseresYSentimientosPage = () => {
	return (
		<main className="bg-black text-white">
			{/* Hero Section */}
			<section
				className="relative py-32 bg-cover bg-center"
				style={{ backgroundImage: "url('/images/sistemas/lasers-y-sentimientos_Header.webp')" }}
			>
				<div className="absolute inset-0 bg-black/60"></div>
				<div className="container mx-auto px-4 max-w-4xl relative z-10">
					<motion.h1
						className="font-amarante text-5xl md:text-7xl mb-6 text-center text-white"
						initial={{ opacity: 0, y: -20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8 }}
					>
						<span className="headerFont inline-block pb-2 border-b-2 border-red-700">
							Láseres y Sentimientos
						</span>
					</motion.h1>

					<motion.p
						className="text-xl text-gray-300 max-w-3xl mx-auto text-center mb-12"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.3, duration: 0.6 }}
					>
						<strong className="">
							Pocas reglas, mucha diversión. Sois la tripulación de la nave espacial Raptor. Vuestra misión,
							explorar nuevos mundos, establecer relación con formas de vida desconocidas (tanto amistosas
							como agresivas), y defender al Consorcio de Planetas de los peligros que acechan en el espacio.
							El Capitán Darcy ha sido poseído por la extraña entidad psíquica conocida como La Otra Cosa.
							Debido a la situación habéis tomado el mando mientras él se recupera en el módulo de enfermería.
						</strong>
					</motion.p>
				</div>
			</section>

			{/* Main Content Section */}
			<section className="py-16 bg-black">
				<div className="container mx-auto px-4 max-w-4xl">
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ duration: 0.6 }}
						className="prose prose-invert max-w-none"
					>
						{/* Historical Context and Evolution */}
						<div className="bg-gray-900 rounded-lg p-8 mb-12 border-2 border-red-900/50">
							<h2 className="text-3xl font-amarante headerFont mb-6" style={{ textAlign: "left" }}>
								<FontAwesomeIcon icon={faSpaceShuttle} className="mr-3 text-red-500" />
								La Space Opera al estilo Star Trek
							</h2>
							<p className="text-gray-300 mb-4">
								Esta es la introducción a <em>Láseres y Sentimientos</em>, un ingenioso juego de rol de una
								página escrito por John Harper. Es gratis y{" "}
								<a
									href="https://www.nosolorol.com/descargas/conbarba/lasers_y_sentimientos.pdf"
									className="text-red-400 hover:text-red-300"
								>
									está online para bajárselo
								</a>{" "}
								así que no tienes excusa para no probar esta space opera al estilo de Star Trek. Las reglas
								son simples y directas, así que es perfecto para principiantes.
							</p>
							<p className="text-gray-300">
								Al principio de cada partida de <em>Láseres y Sentimientos</em>, la historia es decidida
								primero por la creación de los mismos personajes, y segundo por una tabla aleatoria que decide
								el villano, así como sus motivaciones y estrategia. De esta manera, cada partida es diferente
								de la anterior.
							</p>
						</div>

						{/* Game Characteristics */}
						<div className="grid md:grid-cols-2 gap-8 mb-12">
							<div className="bg-gray-800 rounded-lg p-6">
								<h3 className="text-2xl font-amarante text-red-400 mb-4">
									<FontAwesomeIcon icon={faChessKnight} className="mr-3 text-red-500" />
									Filosofía del Juego
								</h3>
								<p className="text-gray-300">
									Aunque la historia tiene una trama general decidida al azar, tus acciones en el juego
									determinarán el curso de la acción. Gracias a la simplicidad de sus reglas, y que cada
									partida dura una única sesión de 2 a 4 horas, <em>Láseres y Sentimientos</em> es el juego
									perfecto para los completos novatos.
								</p>
							</div>
							<div className="bg-gray-800 rounded-lg p-6">
								<h3 className="text-2xl font-amarante text-red-400 mb-4">
									<FontAwesomeIcon icon={faDice} className="mr-3 text-red-500" />
									Basado en la Narrativa
								</h3>
								<p className="text-gray-300">
									Si nunca has jugado a un juego de rol y te interesa adentrarte en el mundillo, con{" "}
									<em>Láseres y Sentimientos</em> puedes aprender las dinámicas básicas de una partida de rol
									sin complicarte con demasiadas reglas. Todas las reglas caben en una sola página.
								</p>
							</div>
						</div>

						{/* Detailed Exploration */}
						<div className="space-y-8">
							<div>
								<h3
									className="text-2xl font-amarante headerFont text-left mb-4"
									style={{ textAlign: "left" }}
								>
									<FontAwesomeIcon icon={faInfinity} className="mr-3 text-red-500" />
									Un Sistema, Infinitos Mundos
								</h3>
								<p className="text-gray-300 mb-4">
									Usando el mismo sistema simplificado de <em>Láseres y Sentimientos</em>, es posible crear
									juegos de rol instantáneos con cualquier temática. Aunque el juego original transcurre en el
									espacio, los fans han creado multitud de «hacks» para adaptar el sistema a otras temáticas:
								</p>
							</div>

							{/* "Hacks" del sistema */}
							<div className="bg-gray-900 rounded-lg p-8">
								<h3 className="text-2xl font-amarante text-red-400 mb-6">
									<FontAwesomeIcon icon={faUsers} className="mr-3 text-red-500" />
									Variaciones del Sistema
								</h3>
								<div className="grid md:grid-cols-2 gap-6">
									<div>
										<ul className="list-none pl-5 text-gray-300 space-y-4">
											<li>
												<h4 className="text-xl text-red-300 mb-2">
													<FontAwesomeIcon icon={faDragon} className="mr-2 text-red-500" />
													Alta Fantasía: Espadas y Hechizos
												</h4>
												<p>
													¿Quieres probar Dragones y Mazmorras, pero prefieres empezar con una versión
													simplificada? En Espadas y Hechizos, podrás vivir aventuras en un mundo de fantasía
													poblado por elfos, orcos, y cualquier criatura que puedas imaginar.
												</p>
											</li>
											<li>
												<h4 className="text-xl text-red-300 mb-2">
													<FontAwesomeIcon icon={faGun} className="mr-2 text-red-500" />
													Pistolas y Pesquisas
												</h4>
												<p>
													Un asesino anda suelto en las calles de Usera. Sois parte de una patrulla especial
													que se ha creado para cazarle, pero vuestro capitán ha desaparecido. Usa tanto tu
													cerebro como tus músculos para hacerte paso en la trama de un show policíaco en las
													calles del Chinatown Madrileño.
												</p>
											</li>
										</ul>
									</div>
									<div>
										<ul className="list-none pl-5 text-gray-300 space-y-4">
											<li>
												<h4 className="text-xl text-red-300 mb-2">
													<FontAwesomeIcon icon={faSkull} className="mr-2 text-red-500" />
													Parches y Pillaje
												</h4>
												<p>
													Sois la tripulación de una fragata pirata, a flote bajo bandera Española. Tu misión
													es navegar por el Mar Caribe, saquear los barcos de las naciones enemigas y mantener
													a la tripulación contenta. ¡Toma las riendas del barco y embárcate en una aventura
													digna de Piratas del Caribe!
												</p>
											</li>
											<li>
												<h4 className="text-xl text-red-300 mb-2">
													<FontAwesomeIcon icon={faBiohazard} className="mr-2 text-red-500" />
													Contagios y Cuarentenas
												</h4>
												<p>
													La civilización ha sido eliminada por culpa de un virus infeccioso, pero un grupo de
													supervivientes tienen la clave para acabar con él. ¿Conseguirán sobrevivir tanto a
													los peligros clásicos de un escenario post-apocalíptico, como a la infección del
													virus? Una aventura para estar preparados para lo que viene.
												</p>
											</li>
										</ul>
									</div>
								</div>
							</div>

							{/* Para quién es Láseres y Sentimientos */}
							<div>
								<h3
									className="text-2xl font-amarante headerFont text-red-500 mb-4"
									style={{ textAlign: "left" }}
								>
									<FontAwesomeIcon icon={faScroll} className="mr-3 text-red-500" />
									¿Qué tipo de jugador es el que mejor se adapta a Láseres y Sentimientos?
								</h3>
								<div className="bg-gray-800 rounded-lg p-6 text-gray-300">
									<p className="mb-4">
										Gracias a la simplicidad de sus reglas, y que cada partida dura una única sesión de 2 a 4
										horas, <em>Láseres y Sentimientos</em> es perfecto para:
									</p>
									<ul className="list-disc pl-5 space-y-2">
										<li>
											<strong>Completos novatos</strong> que nunca han jugado a un juego de rol y les interesa
											adentrarse en el mundillo.
										</li>
										<li>
											<strong>Grupos nuevos</strong> que quieren aprender las dinámicas básicas de una partida
											de rol sin complicarse con demasiadas reglas.
										</li>
										<li>
											<strong>Cualquier persona</strong> interesada en vivir una aventura espacial repleta de
											acción, exploración y misterio en un formato sencillo y accesible.
										</li>
									</ul>
								</div>
							</div>

							{/* Getting Started */}
							<div>
								<h3
									className="text-2xl font-amarante headerFont text-red-500 mb-4"
									style={{ textAlign: "left" }}
								>
									<FontAwesomeIcon icon={faBookOpen} className="mr-3 text-red-500" />
									¡Me encanta! ¿Por dónde empiezo?
								</h3>
								<div className="bg-gray-800 rounded-lg p-6 text-gray-300">
									<p className="mb-4">
										<em>Láseres y Sentimientos</em> es uno de los juegos de rol más fáciles de jugar, incluso
										sin ninguna experiencia como Máster o jugador:
									</p>
									<ul className="list-disc pl-5 space-y-2">
										<li>
											<strong>Descarga las reglas</strong> - Simplemente descarga la versión original en
											inglés, o esta traducción al español. Todo lo que te hace falta para jugar son las
											reglas, que caben en un folio, papel y lápiz para cada jugador, y tres dados de seis
											caras.
											<a
												href="http://onesevendesign.com/lasers_and_feelings_rpg.pdf"
												target="_blank"
												rel="noopener noreferrer"
												className="text-red-400 ml-2 hover:underline"
											>
												<FontAwesomeIcon icon={faLink} className="mr-1" />
												Reglas en inglés (original)
											</a>
											<a
												href="https://www.nosolorol.com/descargas/conbarba/lasers_y_sentimientos.pdf"
												target="_blank"
												rel="noopener noreferrer"
												className="text-red-400 ml-2 hover:underline"
											>
												<FontAwesomeIcon icon={faLink} className="mr-1" />
												Reglas en español
											</a>
										</li>
										<li>
											<strong>Sesión guiada</strong> - Pero si quieres las cosas aún más fáciles, o prefieres
											ver cómo se maneja un Máster experienciado antes de lanzarte, puedes empezar con una
											sesión de 3 horas del juego original, tiempo suficiente para crear los personajes y
											jugar una partida rápida.
											<a
												href="/product/lasers-and-feelings"
												target="_blank"
												rel="noopener noreferrer"
												className="text-red-400 ml-2 hover:underline"
											>
												<FontAwesomeIcon icon={faGamepad} className="mr-1" />
												Reserva tu partida
											</a>
										</li>
									</ul>
								</div>
							</div>
						</div>
					</motion.div>

					{/* CTA Section */}
					<motion.div
						className="mt-16 text-center"
						initial={{ opacity: 0 }}
						whileInView={{ opacity: 1 }}
						viewport={{ once: true }}
					>
						<h3 className="text-3xl font-amarante text-red-400 mb-6">¿Listo para tu aventura espacial?</h3>
						<motion.a
							href={`https://api.whatsapp.com/send?phone=34642367972&text=${encodeURIComponent("Hola, estoy interesado/a en jugar a Láseres y Sentimientos en Madrid. ¿Podéis ayudarme?")}`}
							className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-lg font-amarante text-xl transition-colors"
							whileHover={{
								scale: 1.1,
								boxShadow: "0 0 15px #f93d3d",
								backgroundColor: "white",
								color: "black",
								fontWeight: "900",
								fontStyle: "italic",
							}}
							whileTap={{ scale: 0.98 }}
							target="_blank"
							rel="noopener noreferrer"
						>
							¡A los controles de la Raptor!
						</motion.a>
					</motion.div>
				</div>
			</section>
		</main>
	);
};

export default LaseresYSentimientosPage;
